import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserPreferences } from './useUserPreferences';
import { useUserProgress } from './useUserProgress';

export interface StartHereRecommendation {
  nodeId: string;
  title: string;
  description: string;
  rationale: string;
  confidenceScore: number;
  estimatedTime: number;
  difficultyLevel: number;
  difficultyAlignment: number;
  prerequisitesMet: boolean;
  learningStyleMatch: number;
  contentType: string;
  nodeType: string;
  tags: string[];
  alternativeReason?: string;
}

export interface StartHereAnalysis {
  recommendations: StartHereRecommendation[];
  userProfile: {
    learningStyle: string;
    preferredDifficulty: number;
    studyTimeAvailability: number;
    completedNodes: number;
    disciplineExperience: number;
  };
  disciplineInfo: {
    totalNodes: number;
    difficultyRange: [number, number];
    avgStudyTime: number;
    entryPoints: number;
  };
}

/**
 * Simplified Start Here recommendation algorithm
 * Provides 3-5 optimal entry points based on user preferences and content analysis
 */
export const useStartHereRecommendations = (disciplineId: string) => {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { data: userProgress } = useUserProgress();

  return useQuery({
    queryKey: ['start-here-recommendations', disciplineId, user?.id],
    queryFn: async (): Promise<StartHereAnalysis> => {
      if (!disciplineId || !user?.id) {
        throw new Error('Missing required parameters for Start Here recommendations');
      }

      console.log('🎯 Generating Start Here recommendations for discipline:', disciplineId);

      // Get all nodes for this discipline
      const { data: disciplineNodes, error: nodesError } = await supabase
        .from('nodes')
        .select(`
          id,
          title,
          content,
          content_type,
          node_type,
          difficulty_level,
          estimated_study_time,
          competency_tags,
          learning_objectives,
          created_at
        `)
        .eq('discipline_id', disciplineId)
        .eq('status', 'published')
        .order('difficulty_level', { ascending: true })
        .limit(50); // Limit to improve performance

      if (nodesError) {
        console.error('❌ Error fetching discipline nodes:', nodesError);
        throw nodesError;
      }

      if (!disciplineNodes || disciplineNodes.length === 0) {
        throw new Error('No published nodes found for this discipline');
      }

      // Get user's completed nodes in this discipline
      const userProgressInDiscipline = userProgress?.filter(progress => 
        disciplineNodes.some(node => node.id === progress.node_id)
      ) || [];

      const completedNodeIds = userProgressInDiscipline
        .filter(p => p.status === 'completed')
        .map(p => p.node_id);

      // Simplified user profile
      const userProfile = {
        learningStyle: preferences?.learning_style || 'mixed',
        preferredDifficulty: preferences?.preferred_difficulty || 2,
        studyTimeAvailability: preferences?.study_time_availability || 30,
        completedNodes: completedNodeIds.length,
        disciplineExperience: completedNodeIds.length / disciplineNodes.length
      };

      // Calculate discipline info
      const difficultyLevels = disciplineNodes.map(n => n.difficulty_level || 2);
      const studyTimes = disciplineNodes.map(n => n.estimated_study_time || 30);
      
      const disciplineInfo = {
        totalNodes: disciplineNodes.length,
        difficultyRange: [Math.min(...difficultyLevels), Math.max(...difficultyLevels)] as [number, number],
        avgStudyTime: studyTimes.reduce((a, b) => a + b, 0) / studyTimes.length,
        entryPoints: disciplineNodes.filter(n => (n.difficulty_level || 2) <= 2).length
      };

      // Generate simplified recommendations
      const recommendations = generateSimpleRecommendations(
        disciplineNodes,
        userProfile,
        completedNodeIds
      );

      console.log(`✨ Generated ${recommendations.length} Start Here recommendations`);

      return {
        recommendations,
        userProfile,
        disciplineInfo
      };
    },
    enabled: !!disciplineId && !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1 // Reduce retries to improve performance
  });
};

/**
 * Simplified recommendation generation algorithm
 */
function generateSimpleRecommendations(
  nodes: any[],
  userProfile: any,
  completedNodeIds: string[]
): StartHereRecommendation[] {
  
  // Filter out completed nodes
  const availableNodes = nodes.filter(node => !completedNodeIds.includes(node.id));
  
  if (availableNodes.length === 0) {
    return []; // All nodes completed
  }

  // Simple scoring based on difficulty and entry points
  const scoredNodes = availableNodes.map(node => {
    const difficultyScore = calculateSimpleDifficultyScore(node, userProfile);
    const entryPointScore = calculateSimpleEntryPointScore(node);
    const overallScore = (difficultyScore * 0.7) + (entryPointScore * 0.3);
    
    return {
      node,
      overallScore,
      recommendation: createSimpleRecommendation(node, overallScore, userProfile)
    };
  });

  // Sort by score and take top 5
  scoredNodes.sort((a, b) => b.overallScore - a.overallScore);
  
  return scoredNodes.slice(0, 5).map(item => item.recommendation);
}

/**
 * Simple difficulty scoring
 */
function calculateSimpleDifficultyScore(node: any, userProfile: any): number {
  const nodeDifficulty = node.difficulty_level || 2;
  const userPreference = userProfile.preferredDifficulty;
  
  // Perfect match gets 1.0, decreases with distance
  const distance = Math.abs(nodeDifficulty - userPreference);
  return Math.max(0.2, 1 - (distance / 4));
}

/**
 * Simple entry point scoring
 */
function calculateSimpleEntryPointScore(node: any): number {
  const isFoundational = (node.difficulty_level || 2) <= 2;
  const isOverview = node.node_type === 'concept' || node.content_type === 'foundational';
  
  let score = 0.5; // Base score
  if (isFoundational) score += 0.4;
  if (isOverview) score += 0.3;
  
  return Math.min(1.0, score);
}

/**
 * Create simple recommendation object
 */
function createSimpleRecommendation(node: any, overallScore: number, userProfile: any): StartHereRecommendation {
  const rationale = generateSimpleRationale(node, userProfile);
  
  return {
    nodeId: node.id,
    title: node.title,
    description: typeof node.content === 'string' ? node.content.substring(0, 200) + '...' : 'Learn about this topic',
    rationale,
    confidenceScore: Math.round(overallScore * 100),
    estimatedTime: node.estimated_study_time || 30,
    difficultyLevel: node.difficulty_level || 2,
    difficultyAlignment: calculateSimpleDifficultyScore(node, userProfile),
    prerequisitesMet: (node.difficulty_level || 2) <= 2, // Simplified: foundational nodes don't need prerequisites
    learningStyleMatch: 0.8, // Simplified
    contentType: node.content_type || 'article',
    nodeType: node.node_type || 'article',
    tags: node.competency_tags || []
  };
}

/**
 * Generate simple rationale
 */
function generateSimpleRationale(node: any, userProfile: any): string {
  const difficulty = node.difficulty_level || 2;
  const userDifficulty = userProfile.preferredDifficulty;
  
  if (Math.abs(difficulty - userDifficulty) <= 1) {
    return "Recommended because it matches your preferred difficulty level and provides a solid foundation.";
  } else if (difficulty < userDifficulty) {
    return "Recommended as a gentle introduction to build confidence before tackling more challenging content.";
  } else {
    return "Recommended to provide an appropriate challenge based on your learning preferences.";
  }
} 